import { useState, useRef, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input/input';
import type { Country } from 'react-phone-number-input';
import { getCountries, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import { ChevronDown } from 'lucide-react';

function countryToFlag(code: string) {
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join('');
}

interface PhoneFieldProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  id?: string;
  defaultCountry?: Country;
  className?: string;
  inputClassName?: string;
  selectClassName?: string;
}

export default function PhoneField({
  value,
  onChange,
  id,
  defaultCountry = 'LB',
  className,
  inputClassName,
  selectClassName,
}: PhoneFieldProps) {
  const [country, setCountry] = useState<Country>(() => {
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed?.country) return parsed.country;
      } catch {
        /* fallback */
      }
    }
    return defaultCountry;
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const initializedRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!initializedRef.current && value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed?.country) {
          setCountry(parsed.country);
        }
      } catch {
        /* ignore */
      }
      initializedRef.current = true;
    }
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      searchRef.current?.focus();
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const countries = getCountries();
  const filtered = search
    ? countries.filter(
        (c) =>
          en[c]?.toLowerCase().includes(search.toLowerCase()) ||
          getCountryCallingCode(c).includes(search.replace('+', '')),
      )
    : countries;

  return (
    <div className={`flex gap-2 ${className ?? ''}`}>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-1.5 whitespace-nowrap ${selectClassName ?? ''}`}
        >
          <span className="text-lg leading-none">{countryToFlag(country)}</span>
          <span className="text-fg-secondary text-sm">+{getCountryCallingCode(country)}</span>
          <ChevronDown className="text-fg-subtle h-3.5 w-3.5" />
        </button>

        {open && (
          <div className="border-default bg-surface absolute top-full left-0 z-50 mt-1 w-64 overflow-hidden rounded-xl border shadow-lg">
            <div className="border-subtle border-b p-2">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="border-default focus:border-strong w-full rounded-lg border px-3 py-1.5 text-sm outline-none"
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCountry(c);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`hover:bg-canvas flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                    c === country ? 'bg-canvas font-medium' : ''
                  }`}
                >
                  <span className="text-base leading-none">{countryToFlag(c)}</span>
                  <span className="text-fg truncate">{en[c]}</span>
                  <span className="text-fg-subtle ml-auto tabular-nums">
                    +{getCountryCallingCode(c)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <PhoneInput
        id={id}
        country={country}
        value={value}
        onChange={onChange}
        className={inputClassName}
      />
    </div>
  );
}
